import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { eur } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileDown, Loader2 } from "lucide-react";
import { CourseFilters, MODALITY_LABELS, filterCourses, filterLabel, downloadCoursesPdf } from "@/components/app/CourseFilters";

const empty = { title: "", description: "", category: "Generale", duration_hours: 8, modality: "", audience: "", validity: "", image_url: "", public_price: 0, partner_price: 0, vat_rate: 22, active: true, cfp_enabled: false, cfp_profiles: [] };
const CFP_PROFILES = ["Ingegnere", "Architetto", "Geometra", "Perito Industriale"];


export default function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [mod, setMod] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(() => api.get("/courses").then(r => setCourses(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const categories = useMemo(() => [...new Set(courses.map((c) => c.category).filter(Boolean))].sort(), [courses]);
  const filtered = useMemo(() => filterCourses(courses, q, cat, mod), [courses, q, cat, mod]);
  const [exporting, setExporting] = useState(false);
  const exportPdf = async () => {
    setExporting(true);
    try {
      await downloadCoursesPdf("/export/courses.pdf", filtered.map((c) => c.id), filterLabel(q, cat, mod));
    } catch { toast.error("Esportazione PDF non riuscita"); }
    finally { setExporting(false); }
  };

  const save = async () => {
    if (!form.modality || !["elearning", "videoconferenza", "aula"].includes(form.modality)) {
      toast.error("Seleziona la Tipologia Corso (E-learning, Videoconferenza o Aula)");
      return;
    }
    if (!form.title || form.title.trim().length < 2) {
      toast.error("Il titolo è obbligatorio");
      return;
    }
    if (form.cfp_enabled && (!form.cfp_profiles || form.cfp_profiles.length === 0)) {
      toast.error("Rilascio CFP attivo: seleziona almeno una categoria professionale.");
      return;
    }
    try {
      if (editing) await api.put(`/courses/${editing}`, form);
      else await api.post("/courses", form);
      toast.success(editing ? "Corso aggiornato" : "Corso creato");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
  };

  const del = async (id) => { if (!window.confirm("Eliminare il corso?")) return; await api.delete(`/courses/${id}`); toast.success("Corso eliminato"); load(); };
  const edit = (c) => { setEditing(c.id); setForm({ ...empty, ...c }); setOpen(true); };

  return (
    <div className="space-y-6" data-testid="admin-courses">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Catalogo corsi</h1>
          <p className="text-slate-500 mt-1">Gestisci e-learning, aula e videoconferenza.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={exportPdf} disabled={exporting || filtered.length === 0} data-testid="courses-export-pdf">
            {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />} Esporta PDF
          </Button>
          <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="new-course-button">
            <Plus className="w-4 h-4 mr-1" /> Nuovo corso
          </Button>
        </div>
      </div>
      <CourseFilters q={q} setQ={setQ} cat={cat} setCat={setCat} mod={mod} setMod={setMod} categories={categories} count={filtered.length} />
      <Card className="border-slate-200"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Titolo</TableHead><TableHead>Categoria</TableHead><TableHead>Modalità</TableHead>
            <TableHead>Durata</TableHead><TableHead>Pubblico</TableHead><TableHead>Partner</TableHead>
            <TableHead>IVA</TableHead>
            <TableHead>Stato</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((c) => (
              <TableRow key={c.id} data-testid={`admin-course-row-${c.id}`}>
                <TableCell className="font-medium">{c.title}</TableCell>
                <TableCell>{c.category}</TableCell>
                <TableCell>{MODALITY_LABELS[c.modality] || c.modality}</TableCell>
                <TableCell>{c.duration_hours}h</TableCell>
                <TableCell>{eur(c.public_price)}</TableCell>
                <TableCell className="font-semibold">{eur(c.partner_price)}</TableCell>
                <TableCell><span className={`status-pill ${(c.vat_rate ?? 22) === 0 ? "bg-emerald-100 text-emerald-700" : "bg-indigo-100 text-indigo-700"}`} data-testid={`course-vat-${c.id}`}>{(c.vat_rate ?? 22)}%</span></TableCell>
                <TableCell><span className={`status-pill ${c.active ? "status-confermato" : "status-bozza"}`}>{c.active ? "Attivo" : "Non attivo"}</span></TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => edit(c)} data-testid={`edit-course-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(c.id)} data-testid={`delete-course-${c.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Modifica corso" : "Nuovo corso"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Titolo</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="course-input-title" /></div>
            <div className="col-span-2"><Label>Descrizione</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="course-input-desc" /></div>
            <div><Label>Categoria</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="course-input-cat" /></div>
            <div><Label>Tipologia Corso *</Label>
              <Select value={form.modality} onValueChange={(v) => setForm({ ...form, modality: v })}>
                <SelectTrigger data-testid="course-input-modality" className={!form.modality ? "border-red-300" : ""}><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="elearning">E-learning (solo Credito Partner)</SelectItem>
                  <SelectItem value="videoconferenza">Videoconferenza (PayPal/Bonifico)</SelectItem>
                  <SelectItem value="aula">Aula (PayPal/Bonifico)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Durata (ore)</Label><Input type="number" value={form.duration_hours} onChange={(e) => setForm({ ...form, duration_hours: parseInt(e.target.value || "0") })} /></div>
            <div><Label>Destinatari</Label><Input value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} /></div>
            <div><Label>Validità</Label><Input value={form.validity} onChange={(e) => setForm({ ...form, validity: e.target.value })} /></div>
            <div><Label>Immagine URL</Label><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} /></div>
            <div><Label>Prezzo pubblico €</Label><Input type="number" step="0.01" value={form.public_price} onChange={(e) => setForm({ ...form, public_price: parseFloat(e.target.value || "0") })} data-testid="course-input-public" /></div>
            <div><Label>Prezzo partner €</Label><Input type="number" step="0.01" value={form.partner_price} onChange={(e) => setForm({ ...form, partner_price: parseFloat(e.target.value || "0") })} data-testid="course-input-partner" /></div>
            <div><Label>Aliquota IVA</Label>
              <Select value={String(form.vat_rate ?? 22)} onValueChange={(v) => setForm({ ...form, vat_rate: parseInt(v) })}>
                <SelectTrigger data-testid="course-input-vat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">IVA 0% (esente)</SelectItem>
                  <SelectItem value="22">IVA 22%</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Attivo</Label></div>
            <div className="col-span-2 p-3 rounded-lg border border-amber-200 bg-amber-50/60">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <Label className="font-semibold text-slate-800">Rilascio CFP (Crediti Formativi Professionali)</Label>
                  <div className="text-xs text-slate-500 mt-1">Attiva per permettere ai corsisti di richiedere i CFP in fase di iscrizione.</div>
                </div>
                <Switch
                  checked={!!form.cfp_enabled}
                  onCheckedChange={(v) => setForm({ ...form, cfp_enabled: v, cfp_profiles: v ? (form.cfp_profiles || []) : [] })}
                  data-testid="course-cfp-switch"
                />
              </div>
              {form.cfp_enabled && (
                <div className="mt-3">
                  <Label className="text-xs">Categorie ammesse *</Label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {CFP_PROFILES.map((p) => {
                      const checked = (form.cfp_profiles || []).includes(p);
                      return (
                        <label key={p} className="flex items-center gap-2 p-2 rounded-md border border-slate-200 bg-white cursor-pointer hover:border-amber-300" data-testid={`course-cfp-profile-${p}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const curr = new Set(form.cfp_profiles || []);
                              if (e.target.checked) curr.add(p); else curr.delete(p);
                              setForm({ ...form, cfp_profiles: Array.from(curr) });
                            }}
                            className="h-4 w-4 accent-amber-600"
                          />
                          <span className="text-sm text-slate-800">{p}</span>
                        </label>
                      );
                    })}
                  </div>
                  {(form.cfp_profiles || []).length === 0 && (
                    <div className="text-xs text-red-600 mt-2">Seleziona almeno una categoria.</div>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="save-course">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
